import { t } from '@app/i18n';
import s from './Overview.module.css';

/**
 * Esqueleto de T3 — o desenho de abertura, enquanto nem a leitura em cache chegou
 * (Lote 2b).
 *
 * **A promessa é «sem salto de layout»**, e ela é estrutural, não decorativa: o
 * esqueleto usa as MESMAS classes do conteúdo real (`.kpis`/`.kpi`, `.grade`,
 * `.larga`), com a mesma contagem de blocos e as mesmas alturas. O que o card real
 * ocupa, o esqueleto já ocupa — então quando o dado chega, o texto entra no lugar
 * onde a barra cinza estava, e nada abaixo se move.
 *
 * O que substituiu: até o Lote 2b, T3 abria com um único card «Carregando a visão
 * geral…» de uma linha, e a tela inteira saltava quando os quatro indicadores e os
 * três cards apareciam de uma vez.
 *
 * `aria-busy` + um rótulo só para leitor de tela: quem não vê a animação precisa
 * ouvir «carregando», e quem vê não precisa ler quatro vezes a mesma palavra — por
 * isso as barras são `aria-hidden`.
 */
export function OverviewSkeleton() {
  return (
    // `data-testid` além do `aria-busy`: o E2E precisa de um alvo que seja SÓ o
    // esqueleto, e `[aria-busy="true"]` também casa com o `LoadingButton` do
    // «Discovery agora» enquanto ele roda. Um seletor que casa com duas coisas
    // diferentes vira vermelho por ordem de clique, e o sintoma não parece escopo.
    <div data-testid="overview-esqueleto" aria-busy="true" aria-live="polite">
      <span className={s.srOnly}>{t('overview.page.loading.message')}</span>

      <div className={s.kpis} aria-hidden="true">
        {[0, 1, 2, 3].map((i) => (
          <section key={i} className={s.kpi}>
            <div className={s.kpiHd}>
              <span className={[s.esqueleto, s.esqueletoRotulo].join(' ')} />
            </div>
            <span className={[s.esqueleto, s.esqueletoValor].join(' ')} />
            <span className={[s.esqueleto, s.esqueletoDetalhe].join(' ')} />
          </section>
        ))}
      </div>

      <div className={s.grade} aria-hidden="true">
        <SkeletonCard rows={4} />
        <SkeletonCard rows={4} />
        <SkeletonCard rows={4} />
        <SkeletonCard rows={4} />
        <div className={s.larga}>
          <SkeletonCard rows={6} />
        </div>
      </div>
    </div>
  );
}

/** Um card do esqueleto: cabeçalho + `rows` linhas da altura de `.linha`. */
function SkeletonCard({ rows }: { rows: number }) {
  return (
    <section className={s.esqueletoCard}>
      <span className={[s.esqueleto, s.esqueletoTitulo].join(' ')} />
      {Array.from({ length: rows }, (_, i) => (
        <div key={i} className={s.linha}>
          <span className={[s.esqueleto, s.esqueletoLinha].join(' ')} />
        </div>
      ))}
    </section>
  );
}
